// /electionday — public, read-only live page for voting day +
// counting day. Anyone can view; nothing on this page can trigger
// an upload, an Anthropic call, or any state mutation.
//
// The footage pipeline that used to live here — upload, extract,
// triage, rectify, party-read, cost cap, funnel, per-still review —
// was moved into /admin/electionday on the security-boundary
// correction. The act of sampling is staff-only; the results
// (per-district tallies + projection state) are public, surfaced
// via SWR off the static JSON the admin writes.
//
// Gated by the `featured_election_day` feature flag. Default-deny:
// any status other than 'live' renders the "Not available" stub.
// `IS_LOCAL_DEV` opens a localhost-only bypass so the page can be
// viewed in dev without a DB row — this bypass stays on the public
// page (no auth available here); the admin surface has real
// HMAC-cookie auth and doesn't need a bypass at all.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlags } from '../../lib/useFeatureFlags';
import { useElectionDaySampling } from '../../lib/useElectionDaySampling';
import Header from '../Header';
import Footer from '../Footer';
import StatsPanel from './StatsPanel';
import EditorialResultZone from './EditorialResultZone';
import EditorialDistrictMap from './EditorialDistrictMap';
import CountProgressMap from './CountProgressMap';
import CountProgressTileGrid from './CountProgressTileGrid';
import LiveFeed from './LiveFeed';
import TurnoutHistoryChart from './TurnoutHistoryChart';
import TurnoutTileGrid from './TurnoutTileGrid';
import './ElectionDay.css';

// Local-dev bypass: when running `npm run dev`, render the public
// page regardless of the `featured_election_day` flag so the live
// outputs can be reviewed in dev without a DB row or a deployment.
// `import.meta.env.DEV` is a compile-time constant — Vite replaces
// it with `true` in dev and `false` in every production build, so
// this branch is dead code in every deployed bundle (preview AND
// prod) and the flag's strict default-deny semantics remain fully
// intact off-localhost. The admin surface does NOT carry an
// equivalent bypass; it relies on the real verifySession cookie.
const IS_LOCAL_DEV = import.meta.env.DEV;

export default function ElectionDayPage() {
  const flags = useFeatureFlags();
  const status = flags.featured_election_day?.status;
  const isLive = status === 'live' || IS_LOCAL_DEV;

  // Read the committed sampling tallies the admin publishes. SWR
  // re-polls every 12 s so editor commits appear on the public page
  // without the reader refreshing. `current` is intentionally null —
  // the in-flight uncommitted batch lives on the admin side and
  // never reaches the public surface.
  const { data: sampling } = useElectionDaySampling();

  // ── Cross-map highlight ──
  // The count-progress map's "see lead →" affordance jumps the
  // reader to the matching district on the editorial result map.
  // We own the highlight state here so both siblings see the same
  // value, and we clear it on a timeout so the halo is a brief
  // pulse rather than a sticky stripe.
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const onSeeLead = useCallback((districtId: number) => {
    setHighlightId(districtId);
    const el = document.getElementById('electionday-result-map');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightId(null);
      highlightTimerRef.current = null;
    }, 3200);
  }, []);
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  if (!isLive) {
    return (
      <>
        <Header />
        <main className="electionday-stub">
          <h1 className="electionday-stub__title">Not available</h1>
          <p className="electionday-stub__lede">
            The Election Day live page isn&apos;t open yet.{' '}
            <a href="/" className="electionday-stub__link">
              Go back to the homepage.
            </a>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="electionday-page">
        <div className="electionday-page__hero">
          <p className="electionday-page__kicker">Lovin Malta · Election 2026</p>
          <h1 className="electionday-page__title">Election Day</h1>
          <p className="electionday-page__subtitle">
            Live coverage — voting day turnout, district map, and (once
            counting begins) national PL / PN result tracking.
          </p>
        </div>

        {/* ── Newsroom result zone ──
            Editorial-confirmed national call + per-district first-
            count entries + STV explainer. Kept structurally separate
            from the experimental sample further down — this is
            Lovin Malta's confirmed figure, not the live sample. */}
        <EditorialResultZone />

        {/* ── Turnout zone ──
            National history chart + 13-district tile grid for
            turnout-specific reading. The result map + count-
            progress map sit below in their own paired row. */}
        <section className="electionday-turnout" aria-label="Turnout">
          <header className="electionday-turnout__head">
            <h2 className="electionday-turnout__title">Turnout</h2>
          </header>
          <TurnoutHistoryChart />
          <TurnoutTileGrid />
        </section>

        {/* ── Paired district maps ──
            Editorial result map (left, partisan lean + 2022 base-
            line + count-progress ring) + count-progress map (right,
            apolitical amber ramp, same district geography). Desktop:
            1 fr 1 fr row; mobile: stacked, result map above per
            spec. Both sourced from the same editorial form; experi-
            mental sampling never touches either. */}
        <div className="electionday-paired">
          <EditorialDistrictMap highlightId={highlightId} />
          <CountProgressMap onSeeLead={onSeeLead} />
        </div>

        {/* Tile-grid equivalent of the count-progress map for
            quick-scan readers. Same editorial data, same see-lead
            wiring back to the result map. */}
        <CountProgressTileGrid onSeeLead={onSeeLead} />

        {/* Live newsroom + social feed — manual publishing. */}
        <LiveFeed />

        {/* ── Experimental sample zone (read-only) ──
            Sampling pipeline lives in /admin/electionday; this page
            renders only the resulting tallies + projection state via
            the StatsPanel. No controls, no upload, no model calls. */}
        <div className="electionday-experimental-divider">
          <h2 className="electionday-experimental-divider__title">
            Our experimental real-time sample
          </h2>
          <p className="electionday-experimental-divider__lede">
            A separate, live sample drawn from video of ballots being
            counted by our newsroom team. Statistical only —
            indicative, not a call. The numbers below are independent
            of the newsroom result above and are never averaged
            together.
          </p>
        </div>
        <StatsPanel committed={sampling.committedBatches} current={null} />
      </main>
      <Footer />
    </>
  );
}
